import {
  QUERY_MATCH_RANK_EXACT,
  QUERY_MATCH_RANK_NONE,
  QUERY_MATCH_RANK_PREFIX,
  QUERY_MATCH_RANK_SUBSTRING,
  compactQueryMatchText,
  flattenQueryMatchValues,
  matchPreparedQueryValues,
  prepareQueryMatchState,
} from "./match.js";

// Shared query selection: retain every matching identity, ordered by relevance.
export function matchingQueryCatalogRecords(index, value, accepts = () => true) {
  return queryCatalogCandidates(index, value)
    .filter(accepts)
    .map(record => ({ record, match: queryCatalogRecordMatch(index, record, value) }))
    .filter(({ match }) => match.rank > 0)
    .sort((a, b) => b.match.rank - a.match.rank || b.match.score - a.match.score)
    .map(({ record }) => record);
}

function addIndexEntry(map, key, recordIndex) {
  if (!key) {
    return;
  }
  const entries = map.get(key);
  if (entries) {
    if (entries[entries.length - 1] !== recordIndex) {
      entries.push(recordIndex);
    }
    return;
  }
  map.set(key, [recordIndex]);
}

function preparedRecordValues(values, deduplicate = true) {
  const seen = new Set();
  const prepared = [];
  for (const value of flattenQueryMatchValues(values)) {
    const raw = String(value ?? "").trim();
    if (!raw || (deduplicate && seen.has(raw))) {
      continue;
    }
    if (deduplicate) {
      seen.add(raw);
    }
    const matchState = prepareQueryMatchState(raw);
    prepared.push(matchState);
  }
  return prepared;
}

function compactSubsequenceIncludes(candidate, query) {
  if (!candidate || !query || query.length > candidate.length) {
    return false;
  }
  let cursor = 0;
  for (const character of query) {
    cursor = candidate.indexOf(character, cursor);
    if (cursor < 0) {
      return false;
    }
    cursor += 1;
  }
  return true;
}

function queryState(query) {
  return prepareQueryMatchState(query);
}

function recordsAtSortedIndices(index, indices) {
  return [...indices]
    .sort((left, right) => left - right)
    .map((recordIndex) => index.records[recordIndex]);
}

function oneCharacterMatchForValue(value, character, valueIndex) {
  const exact = value.normalized === character || value.slug === character;
  if (exact) {
    return {
      rank: QUERY_MATCH_RANK_EXACT,
      score: 100000 - valueIndex / 1000,
    };
  }
  const prefix =
    value.normalized.startsWith(character) ||
    value.slug.startsWith(character) ||
    value.compact.startsWith(character);
  if (prefix) {
    return {
      rank: QUERY_MATCH_RANK_PREFIX,
      score: 80000 - Math.max(0, value.compact.length - 1) - valueIndex / 1000,
    };
  }
  const index = Math.min(
    ...[
      value.normalized.indexOf(character),
      value.slug.indexOf(character),
      value.compact.indexOf(character),
    ].filter((candidate) => candidate >= 0),
  );
  return {
    rank: QUERY_MATCH_RANK_SUBSTRING,
    score: 60000 - Math.min(index, 200) - valueIndex / 1000,
  };
}

function recordOneCharacterMatch(values, character) {
  let best = null;
  values.forEach((value, valueIndex) => {
    if (!value.compact.includes(character)) {
      return;
    }
    const candidate = oneCharacterMatchForValue(value, character, valueIndex);
    if (
      !best ||
      candidate.rank > best.rank ||
      (candidate.rank === best.rank && candidate.score > best.score)
    ) {
      best = candidate;
    }
  });
  return best;
}

function compareOneCharacterCandidates(left, right, labels) {
  if (right.rank !== left.rank) {
    return right.rank - left.rank;
  }
  if (right.score !== left.score) {
    return right.score - left.score;
  }
  const labelOrder = labels[left.recordIndex].localeCompare(labels[right.recordIndex]);
  return labelOrder || left.recordIndex - right.recordIndex;
}

export function buildQueryCatalogIndex(sourceRecords, options = {}) {
  const exactOnly = options.mode === "exact";
  const prefixOnly = options.mode === "prefix";
  const scanOnly = options.mode === "scan";
  const records = [];
  const preparedValues = [];
  const oneCharacterPreparedValues = [];
  const oneCharacterLabels = [];
  const oneCharacterIdentityKeys = [];
  const exactNormalized = new Map();
  const exactSlug = new Map();
  const prefixCharacterBuckets = new Map();
  const compactCharacterBuckets = new Map();
  const recordIndices = new WeakMap();

  for (const sourceRecord of Array.isArray(sourceRecords) ? sourceRecords : []) {
    if (!sourceRecord || typeof sourceRecord !== "object") {
      continue;
    }
    const recordIndex = records.length;
    // Preserve source order and duplicates because the final matcher uses the
    // value position as a deterministic score tie-breaker.
    const values = Array.isArray(sourceRecord.preparedValues)
      ? sourceRecord.preparedValues
      : preparedRecordValues(sourceRecord.values, false);
    const oneCharacter = sourceRecord.oneCharacter && typeof sourceRecord.oneCharacter === "object"
      ? sourceRecord.oneCharacter
      : null;
    records.push(sourceRecord);
    recordIndices.set(sourceRecord, recordIndex);
    if (!exactOnly) {
      preparedValues.push(values);
    }
    if (!exactOnly && !prefixOnly) {
      oneCharacterLabels.push(String(oneCharacter?.label ?? sourceRecord.key ?? ""));
      oneCharacterIdentityKeys.push(String(oneCharacter?.identityKey ?? ""));
    }
    if (oneCharacter && !exactOnly && !prefixOnly) {
      const oneCharacterSourceValues = Object.prototype.hasOwnProperty.call(oneCharacter, "values")
        ? oneCharacter.values
        : sourceRecord.values;
      const oneCharacterValues = oneCharacterSourceValues === sourceRecord.values
        ? values
        : preparedRecordValues(oneCharacterSourceValues, false);
      oneCharacterPreparedValues.push(oneCharacterValues);
    } else if (!exactOnly && !prefixOnly) {
      oneCharacterPreparedValues.push([]);
    }
    const recordCharacters = exactOnly || prefixOnly || scanOnly ? null : new Set();
    const recordPrefixCharacters = exactOnly || scanOnly ? null : new Set();

    for (const value of values) {
      if (!scanOnly) {
        addIndexEntry(exactNormalized, value.normalized, recordIndex);
        addIndexEntry(exactSlug, value.slug, recordIndex);
      }
      if (recordCharacters && value.compact) {
        for (const character of value.compact) {
          recordCharacters.add(character);
        }
      }
      if (recordPrefixCharacters) {
        for (const character of [
          value.normalized.charAt(0),
          value.slug.charAt(0),
          value.compact.charAt(0),
        ]) {
          if (character) {
            recordPrefixCharacters.add(character);
          }
        }
      }
    }

    if (recordCharacters) {
      for (const character of recordCharacters) {
        addIndexEntry(compactCharacterBuckets, character, recordIndex);
      }
    }
    if (recordPrefixCharacters) {
      for (const character of recordPrefixCharacters) {
        addIndexEntry(prefixCharacterBuckets, character, recordIndex);
      }
    }
  }

  return {
    mode: options.mode || "indexed",
    records,
    preparedValues,
    oneCharacterPreparedValues,
    oneCharacterMatchCache: new Map(),
    oneCharacterLabels,
    oneCharacterIdentityKeys,
    exactNormalized,
    exactSlug,
    prefixCharacterBuckets,
    compactCharacterBuckets,
    recordIndices,
  };
}

export function createQueryCatalogIndexCache(buildRecord, options = {}) {
  const cache = new WeakMap();
  return (entries) => {
    if (!Array.isArray(entries)) {
      return buildQueryCatalogIndex([], options);
    }
    const cached = cache.get(entries);
    // Production catalogs are immutable revision snapshots. The length check also
    // tolerates append-only test/admin inputs, but same-length in-place edits need
    // a new array (or an upstream revision projection) to invalidate this cache.
    if (cached?.length === entries.length) {
      return cached.index;
    }
    const records = [];
    entries.forEach((entry, entryIndex) => {
      const record = buildRecord(entry, entryIndex);
      if (record) {
        records.push(record);
      }
    });
    const index = buildQueryCatalogIndex(records, options);
    cache.set(entries, { length: entries.length, index });
    return index;
  };
}

// Exact and prefix lookups narrow by the matcher's normalized forms. Callers still own
// final scoring, identity deduplication, and result limits.
export function queryCatalogExactCandidates(index, query) {
  if (!index?.records?.length) {
    return [];
  }
  const state = queryState(query);
  if (index.mode === "scan") {
    return index.records.filter((_record, recordIndex) =>
      index.preparedValues[recordIndex].some((value) =>
        (state.normalized && value.normalized === state.normalized) ||
        (state.slug && value.slug === state.slug)
      )
    );
  }
  const indices = new Set([
    ...(index.exactNormalized.get(state.normalized) || []),
    ...(index.exactSlug.get(state.slug) || []),
  ]);
  return recordsAtSortedIndices(index, indices);
}

export function queryCatalogRecordMatch(index, record, query, options = {}) {
  const recordIndex = index?.recordIndices?.get(record);
  return matchPreparedQueryValues(
    Number.isInteger(recordIndex) ? index.preparedValues[recordIndex] : [],
    query,
    options,
  );
}

export function queryCatalogPrefixCandidates(index, query) {
  if (!index?.records?.length) {
    return [];
  }
  const state = queryState(query);
  if (index.mode === "scan") {
    return index.records.filter((_record, recordIndex) =>
      index.preparedValues[recordIndex].some((value) =>
        (state.normalized && value.normalized.startsWith(state.normalized)) ||
        (state.slug && value.slug.startsWith(state.slug)) ||
        (state.compact && value.compact.startsWith(state.compact))
      )
    );
  }
  const indices = new Set();
  for (const character of new Set([
    state.normalized.charAt(0),
    state.slug.charAt(0),
    state.compact.charAt(0),
  ])) {
    for (const recordIndex of index.prefixCharacterBuckets.get(character) || []) {
      indices.add(recordIndex);
    }
  }
  return [...indices]
    .sort((left, right) => left - right)
    .filter((recordIndex) => index.preparedValues[recordIndex].some((value) =>
      (state.normalized && value.normalized.startsWith(state.normalized)) ||
      (state.slug && value.slug.startsWith(state.slug)) ||
      (state.compact && value.compact.startsWith(state.compact))
    ))
    .map((recordIndex) => index.records[recordIndex]);
}

function rankedOneCharacterCandidates(index, character, prefixOnly) {
  const cacheKey = `${prefixOnly ? "prefix" : "any"}:${character}`;
  const cached = index.oneCharacterMatchCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const candidates = [];
  for (let recordIndex = 0; recordIndex < index.records.length; recordIndex += 1) {
    const match = recordOneCharacterMatch(
      index.oneCharacterPreparedValues[recordIndex],
      character,
    );
    if (match && (!prefixOnly || match.rank >= QUERY_MATCH_RANK_PREFIX)) {
      candidates.push({ recordIndex, ...match });
    }
  }
  candidates.sort((left, right) =>
    compareOneCharacterCandidates(left, right, index.oneCharacterLabels)
  );
  index.oneCharacterMatchCache.set(cacheKey, candidates);
  return candidates;
}

// One-character queries are common while typing and can match most of a catalog.
// The index owns their deterministic ranking so callers only walk as far as the
// requested result count. Dynamic exclusions can consume early winners; continuing
// through the ranked list expands the shortlist without rescanning or resorting.
export function queryCatalogOneCharacterCandidates(index, query, options = {}) {
  if (!index?.records?.length) {
    return [];
  }
  const character = compactQueryMatchText(query);
  if (character.length !== 1) {
    return [];
  }
  const limit = Number.parseInt(options.limit, 10);
  const boundedLimit = limit > 0 ? limit : Number.MAX_SAFE_INTEGER;
  const prefixOnly = options.prefixOnly === true;
  const records = [];
  const seenIdentityKeys = new Set();
  const candidates = rankedOneCharacterCandidates(index, character, prefixOnly);
  for (const candidate of candidates) {
    if (candidate.rank <= QUERY_MATCH_RANK_NONE) {
      continue;
    }
    const record = index.records[candidate.recordIndex];
    if (options.includeRecord && options.includeRecord(record) !== true) {
      continue;
    }
    const identityKey = index.oneCharacterIdentityKeys[candidate.recordIndex];
    if (identityKey) {
      if (seenIdentityKeys.has(identityKey)) {
        continue;
      }
      seenIdentityKeys.add(identityKey);
    }
    records.push(record);
    if (records.length >= boundedLimit) {
      break;
    }
  }
  return records;
}

// This returns a semantics-safe superset, including fuzzy subsequences. It must not be
// treated as a final match result without applying the owning matcher.
export function queryCatalogCandidates(index, query) {
  if (!index?.records?.length) {
    return [];
  }
  const compact = compactQueryMatchText(query);
  if (!compact) {
    return [...index.records];
  }
  if (index.mode === "scan") {
    return index.records.filter((_record, recordIndex) =>
      index.preparedValues[recordIndex]
        .some((value) => compactSubsequenceIncludes(value.compact, compact))
    );
  }

  let seed = null;
  for (const character of new Set(compact)) {
    const bucket = index.compactCharacterBuckets.get(character) || [];
    if (!bucket.length) {
      return [];
    }
    if (!seed || bucket.length < seed.length) {
      seed = bucket;
    }
  }

  return (seed || [])
    .filter((recordIndex) => index.preparedValues[recordIndex]
      .some((value) => compactSubsequenceIncludes(value.compact, compact)))
    .map((recordIndex) => index.records[recordIndex]);
}

// Exact identities win over prefixes. Multiple records may describe one identity;
// the domain supplies that identity and can exclude records outside its scope.
export function resolveQueryCatalogReference(indexes, query, identity, prefer = () => false) {
  if (!indexes.length) return { status: "catalog_unavailable" };
  for (const candidates of [queryCatalogExactCandidates, queryCatalogPrefixCandidates]) {
    const matches = new Map();
    for (const index of indexes) {
      for (const record of candidates(index, query)) {
        const key = identity(record);
        if (key && (!matches.has(key) || prefer(record, matches.get(key)))) matches.set(key, record);
      }
    }
    if (matches.size === 1) return { status: "resolved", record: matches.values().next().value };
    if (matches.size > 1) return { status: "ambiguous" };
  }
  return { status: "unknown" };
}
